/**
 * yd-film: 3×3 九宫格整体等比放大（GSAP ScrollTrigger，本地全局变量）。
 * 源自 web-hand-off film.js：import→window.gsap；删 partials:loaded，补 shopify:section:load。
 * 黑屏命题滑入→停留→九宫格渐显→整组以中心为原点 scale 放大、中心视频铺满并自动播放。
 * 无 GSAP / reduced-motion：静态九宫格、命题隐藏、不放大。
 *
 * GSAP 评审优化（2026-06-14）：
 * - 放大动画只走 transform(scale)，不再每帧动画 grid 的 gap（layout 属性，scrub 中会触发 reflow → 卡顿）。
 *   gap 保留为静态小值；放大到中心格铺满时周围 8 格已移出视口，gap 自然不可见，视觉等价且零 reflow。
 * - 每个 ScrollTrigger 带 id，shopify:section:unload 时 kill，避免主题编辑器重排 section 后在 stale DOM 上空转。
 */
(function () {
  var gsap = window.gsap, ScrollTrigger = window.ScrollTrigger;

  // 移动端（≤860）不加载 film 资源：data-src 不赋值，浏览器永不请求图片/视频（含 7MB 中心视频），整段由 CSS 隐藏。
  function isMobile() { return window.matchMedia && window.matchMedia('(max-width: 860px)').matches; }

  // PC 才把 data-src 激活为真实 src（懒加载，移动端跳过）
  function activateAssets(grid) {
    if (grid.dataset.filmAssetsOn) return;
    grid.dataset.filmAssetsOn = '1';
    grid.querySelectorAll('img[data-src]').forEach(function (img) {
      var src = img.getAttribute('data-src');
      if (src) img.src = src;
    });
    grid.querySelectorAll('video[data-poster]').forEach(function (v) { v.poster = v.getAttribute('data-poster'); v.preload = 'auto'; });
    grid.querySelectorAll('source[data-src]').forEach(function (s) {
      var src = s.getAttribute('data-src');
      if (src) s.src = src;
    });
    var vid = grid.querySelector('video[data-film-video]');
    if (vid) { try { vid.load(); } catch (e) {} }
  }

  // 移动端：命题入场淡入 + 命题后竖版视频（激活竖版资源、进视口播放）；九宫格不加载、不动画
  function setupMobile(grid) {
    if (grid.dataset.filmReady) return;
    grid.dataset.filmReady = '1';
    var section = grid.closest('.film');
    if (!section) return;
    var thesis = section.querySelector('[data-film-thesis]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hasIO = 'IntersectionObserver' in window;

    // 1) 命题淡入
    if (thesis) {
      if (reduce || !hasIO) { thesis.style.opacity = '1'; }
      else {
        thesis.style.opacity = '0';
        thesis.style.transition = 'opacity 0.7s ' + (getComputedStyle(document.documentElement).getPropertyValue('--kw-ease') || 'ease');
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { thesis.style.opacity = '1'; io.unobserve(e.target); } });
        }, { threshold: 0.3 });
        io.observe(thesis);
      }
    }

    // 2) 命题后竖版视频：激活 data-src（移动端才下载），进视口自动播放、出视口暂停（省电）
    var portrait = section.querySelector('[data-film-portrait]');
    if (portrait && !portrait.dataset.portraitOn) {
      portrait.dataset.portraitOn = '1';
      if (portrait.getAttribute('data-poster')) portrait.poster = portrait.getAttribute('data-poster');
      var src = portrait.querySelector('source[data-src]');
      if (src) src.src = src.getAttribute('data-src');
      try { portrait.load(); } catch (e) {}
      if (reduce || !hasIO) {
        // reduced-motion：静止首帧，不自动播
        portrait.addEventListener('loadeddata', function () { try { portrait.currentTime = 0; portrait.pause(); } catch (e) {} });
      } else {
        var vio = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { var p = portrait.play(); if (p && p.catch) p.catch(function () {}); }
            else { portrait.pause(); }
          });
        }, { threshold: 0.25 });
        vio.observe(portrait);
      }
    }
  }

  function setup(grid) {
    if (grid.dataset.filmReady) return;
    // 移动端：只跑命题入场，九宫格不加载、不建 ScrollTrigger（省 7MB 视频等资源）
    if (isMobile()) { setupMobile(grid); return; }
    grid.dataset.filmReady = '1';
    activateAssets(grid);   // PC：激活 data-src → src（开始下载图片/视频）
    var wrap = grid.parentNode;
    var pin = wrap.parentNode;
    var section = grid.closest('.film') || pin.parentNode;
    var sid = section && section.id ? section.id : 'film';
    var thesis = section ? section.querySelector('[data-film-thesis]') : null;
    var video = grid.querySelector('[data-film-video]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !gsap || !ScrollTrigger) {
      grid.style.opacity = '1';
      if (thesis) thesis.style.opacity = '0';
      return;
    }
    if (thesis) {
      var tt = gsap.timeline({ scrollTrigger: { id: 'yd-film-thesis-' + sid, trigger: pin, start: 'top bottom', end: 'top top', scrub: true } });
      tt.fromTo(thesis, { opacity: 0, y: 64 }, { opacity: 1, y: 0, ease: 'none', duration: 0.30 }, 0);
      tt.to(thesis, { opacity: 0, y: -64, ease: 'none', duration: 0.28 }, 0.45);
    }
    gsap.fromTo(grid, { opacity: 0 }, { opacity: 1, ease: 'none', scrollTrigger: { id: 'yd-film-fade-' + sid, trigger: pin, start: 'top 52%', end: 'top 16%', scrub: true } });
    function targetScale() {
      var cell = grid.querySelector('.film__cell');
      var cw = cell.offsetWidth, ch = cell.offsetHeight;
      if (!cw || !ch) return 3.2;
      return Math.max(window.innerWidth / cw, window.innerHeight / ch) * 1.02;
    }
    function playVideo() { if (video) { var p = video.play(); if (p && p.catch) p.catch(function () {}); } }
    function resetVideo() { if (video) { video.pause(); video.currentTime = 0; } }
    var tl = gsap.timeline({
      scrollTrigger: {
        id: 'yd-film-scale-' + sid,
        trigger: pin, start: 'top top', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true,
        onEnter: playVideo, onEnterBack: playVideo, onLeaveBack: resetVideo
      }
    });
    // 只动 scale（transform，compositor，零 reflow）；gap 由 CSS 静态保留
    tl.fromTo(grid, { scale: 1 }, { scale: targetScale, ease: 'none', duration: 0.7 }, 0);
    tl.to(grid, { scale: targetScale, ease: 'none', duration: 0.3 }, 0.7);
  }
  function init() {
    if (window.gsap && window.ScrollTrigger) { gsap = window.gsap; ScrollTrigger = window.ScrollTrigger; gsap.registerPlugin(ScrollTrigger); }
    document.querySelectorAll('[data-film-grid]').forEach(setup);
  }
  function cleanup(e) {
    var sec = e && e.target ? e.target : null;
    if (!sec || !window.ScrollTrigger) return;
    ScrollTrigger.getAll().forEach(function (st) {
      if (st.vars && typeof st.vars.id === 'string' && st.vars.id.indexOf('yd-film-') === 0 && (!st.trigger || sec.contains(st.trigger))) st.kill();
    });
    var grid = sec.querySelector('[data-film-grid]');
    if (grid) delete grid.dataset.filmReady;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', cleanup);
  window.addEventListener('load', function () { init(); if (window.ScrollTrigger) window.ScrollTrigger.refresh(); });
})();
